// Derived rollups over a ClaimProjection's months. These values are NOT
// stored on the projection — recomputed on demand from the month stream so
// the in-memory shape stays minimal and identities can't drift.
//
// All helpers are pure. UI code that needs a running cumulative typically
// wants the `*Series` variants (one value per month, same length as
// `months`); single-number aggregates use `total*` / `final*`.

export function getLtcPool(state: MonthlyState): number {
  return state.pools.baseBenefitPool + state.pools.extensionOfBenefitsPool;
}

function getEpLumpSumPaid(month: MonthlyEntry): number {
  const { strategyDeltas } = month.deltas;
  return typeof (strategyDeltas as { epLumpSumPaid?: number })?.epLumpSumPaid === 'number'
    ? (strategyDeltas as { epLumpSumPaid: number }).epLumpSumPaid
    : 0;
}

// Reads a numeric strategy-delta field (CM II surplus flow), defaulting to 0
// for strategies that don't emit it. Mirrors `getEpLumpSumPaid`.
function readStrategyDelta(month: MonthlyEntry, key: string): number {
  const slot = month.deltas.strategyDeltas as Record<string, unknown> | undefined;
  const value = slot?.[key];
  return typeof value === 'number' ? value : 0;
}

// Dollars paid by the policy in a single month (regular indemnity coverage
// plus any retroactive lump in strategyDeltas). Matches the predicate
// previously used to derive `firstPayoutRowIndex`.
export function getMonthPayout(month: MonthlyEntry): number {
  const { payout } = month.deltas;
  // `ltcCoveragePaid` is cost-covered dollars and can overlap with
  // `indemnityPayout`; monthly cash payout should count only one base payout
  // stream plus any EP lump.
  const basePayout = Math.max(payout.indemnityPayout, payout.ltcCoveragePaid);
  return basePayout + getEpLumpSumPaid(month);
}

export function getFirstPayoutMonthIndex(months: MonthlyEntry[]): number | null {
  for (let i = 0; i < months.length; i += 1) {
    if (getMonthPayout(months[i]) > 0) return i;
  }
  return null;
}

// For joint projections, each spouse has their own "benefits begin" boundary
// (partner may start claiming long after the client has been paying). Returns
// the index in `months` of each claimant's first paying row. Rows that never
// pay don't get an entry — callers should treat "no entry" as "every row for
// this claimant is out-of-pocket." Single-life rows (claimant == null) are
// bucketed under the sentinel key `__single__` so a single map covers both
// shapes uniformly.
const SINGLE_LIFE_CLAIMANT_KEY = '__single__';

export function getFirstPayoutIndexByClaimant(
  months: MonthlyEntry[],
): Map<string, number> {
  const out = new Map<string, number>();
  for (let i = 0; i < months.length; i += 1) {
    const key = months[i].context.claimant ?? SINGLE_LIFE_CLAIMANT_KEY;
    if (out.has(key)) continue;
    if (getMonthPayout(months[i]) > 0) out.set(key, i);
  }
  return out;
}

// Lookup key for `getFirstPayoutIndexByClaimant` — exported so consumers
// can resolve a row without re-stringifying the sentinel themselves.
export function claimantKey(month: MonthlyEntry): string {
  return month.context.claimant ?? SINGLE_LIFE_CLAIMANT_KEY;
}

// Per-month cumulative LTC payout (running sum of indemnity + cost-coverage
// + retroactive lump). Final element is the lifetime total.
export function getCumulativeLtcPayoutSeries(months: MonthlyEntry[]): number[] {
  const out = new Array<number>(months.length);
  let running = 0;
  for (let i = 0; i < months.length; i += 1) {
    running += getMonthPayout(months[i]);
    out[i] = running;
  }
  return out;
}

export function getTotalLtcPayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += getMonthPayout(m);
  return total;
}

// Per-month cumulative out-of-pocket cost (running sum of payout.ineligibleCost).
export function getCumulativeIneligibleCostSeries(months: MonthlyEntry[]): number[] {
  const out = new Array<number>(months.length);
  let running = 0;
  for (let i = 0; i < months.length; i += 1) {
    running += months[i].deltas.payout.ineligibleCost;
    out[i] = running;
  }
  return out;
}

export function getTotalIneligibleCost(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.ineligibleCost;
  return total;
}

// ──────────────────────────────────────────────────────────────────────────
// LTC vs Non-LTC split rollups — match the funding-table taxonomy and the
// legacy engine's classification:
//   LTC payout      = dollars applied to care cost (ltcPayoutToCost + EP lump);
//                     includes banked surplus later drained to cover cost
//   Non-LTC payout  = indemnity surplus REMAINING at horizon (never used for
//                     care) + death benefit remaining at horizon
//
// Sum of getTotalLtcCoveragePayout + getTotalNonLtcPayout = getTotalPayout.
// ──────────────────────────────────────────────────────────────────────────

// Sum of dollars the policy applied to care cost: `ltcPayoutToCost` (direct
// cost coverage PLUS any banked indemnity surplus later drained to cover a
// shortfall) + any retroactive EP lump. Uses `ltcPayoutToCost`, not
// `ltcCoveragePaid`, to match the legacy `projectedCostCoverage` accumulator,
// which folds drained surplus into coverage (`applyIndemnityPayoutToPhase` →
// `costCoverageAddedToTotal`). For reimbursement products the two are equal
// (no surplus), so only CM II is affected. The drained surplus is therefore
// classified as LTC here and excluded from `getTotalIndemnitySurplus` below —
// the two stay complementary so `getTotalPayout` is unchanged.
export function getTotalLtcCoveragePayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.ltcPayoutToCost + getEpLumpSumPaid(m);
  return total;
}

export function getTotalIndemnityPayout(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.deltas.payout.indemnityPayout;
  return total;
}

// Indemnity surplus remaining at the end of the projection — the carried
// balance, NOT the gross accrual. Surplus that was later drained to cover care
// cost is counted under `getTotalLtcCoveragePayout` (as `ltcPayoutToCost`), so
// only the never-used remainder is the non-LTC cash component. This matches
// legacy, where `ltcPolicy.indemnitySurplus` is the accumulator's final value
// (`accumulator.indemnitySurplus += accrued − applied`, surfaced at
// `calcFundingSourceDerivatives.ts`). The balance telescopes to
// `Σ surplusAccrued − Σ surplusApplied` over the month stream (per-row
// identity `close = open + accrued − applied`); clamped at 0 defensively.
export function getTotalIndemnitySurplus(months: MonthlyEntry[]): number {
  let accrued = 0;
  let applied = 0;
  for (const m of months) {
    accrued += readStrategyDelta(m, 'indemnitySurplusAccrued');
    applied += readStrategyDelta(m, 'indemnitySurplusApplied');
  }
  return Math.max(accrued - applied, 0);
}

// Death benefit pool remaining at the end of the projection. Paid to
// beneficiaries on death; counts as a non-LTC return for ROI purposes.
export function getRemainingDeathBenefit(months: MonthlyEntry[]): number {
  if (months.length === 0) return 0;
  return months[months.length - 1].state.pools.deathBenefit;
}

export function getTotalNonLtcPayout(months: MonthlyEntry[]): number {
  return getTotalIndemnitySurplus(months) + getRemainingDeathBenefit(months);
}

// LTC coverage + non-LTC payout (indemnity surplus + remaining DB).
// This is the "total returns" figure that would feed an ROI calculation.
export function getTotalPayout(months: MonthlyEntry[]): number {
  return getTotalLtcCoveragePayout(months) + getTotalNonLtcPayout(months);
}

// Total inflation-adjusted care cost incurred across the projection,
// regardless of who paid. Equals policy LTC coverage + ineligible cost
// (modulo retroactive lump offsets).
export function getTotalProjectedCareCost(months: MonthlyEntry[]): number {
  let total = 0;
  for (const m of months) total += m.facts.effectiveCareCost;
  return total;
}
